// marks.tsx — THE RULE mark set, lifted VERBATIM from the Claude Design delivery
// (project 337c14f7, board "03 · Marks — one grammar, twelve glyphs").
//
// The grammar, in the designer's words: wall rule + route line + apex + tally.
// Change one part, change the meaning. 24px grid, 1.7px stroke, round joins.
// No circle, no tick — "a circle with a tick is a pictogram, not a mark".
//
// 🔴 These render as INLINE <svg>, never <img src={...}>. P6c row 3: an SVG carrying
// stroke="currentColor" inside an <img> document resolves to BLACK and disappears on a
// dark ground — that is exactly how Study shipped broken 2026-08-06. Inline, currentColor
// inherits the CSS color, which .mark--send / .mark--attempt / .mark--project set.
import React from "react";

export type MarkId = "onsight" | "flash" | "redpoint" | "repeat" | "attempt";
export type DisciplineId = "boulder" | "sport" | "trad" | "toprope" | "other";
export type EnvId = "indoor" | "outdoor";

const P: Record<string, React.ReactNode> = {
  "m-onsight": (<><g style={{"fill":"none","stroke":"currentColor","strokeWidth":"1.7","strokeLinecap":"round","strokeLinejoin":"round"}}>
        <line x1="3" y1="3" x2="3" y2="19"></line>
        <circle cx="6.6" cy="18" r="1.7"></circle>
        <line x1="8" y1="16.6" x2="16.4" y2="7.4"></line>
        <path d="M18.2 3.4 L21.4 6.6 L18.2 9.8 L15 6.6 Z" style={{"fill":"currentColor","stroke":"currentColor"}}></path>
        <line x1="6" y1="22" x2="8" y2="22"></line>
      </g></>),
  "m-flash": (<><g style={{"fill":"none","stroke":"currentColor","strokeWidth":"1.7","strokeLinecap":"round","strokeLinejoin":"round"}}>
        <line x1="3" y1="3" x2="3" y2="19"></line>
        <circle cx="6.6" cy="18" r="1.7" style={{"fill":"currentColor"}}></circle>
        <line x1="8" y1="16.6" x2="16.4" y2="7.4"></line>
        <path d="M18.2 3.4 L21.4 6.6 L18.2 9.8 L15 6.6 Z" style={{"fill":"currentColor","stroke":"currentColor"}}></path>
        <line x1="8.4" y1="12.6" x2="15.2" y2="12.6"></line>
        <line x1="6" y1="22" x2="8" y2="22"></line>
      </g></>),
  "m-redpoint": (<><g style={{"fill":"none","stroke":"currentColor","strokeWidth":"1.7","strokeLinecap":"round","strokeLinejoin":"round"}}>
        <line x1="3" y1="3" x2="3" y2="17"></line>
        <polyline points="6.4,16.6 10.6,12.4 8.2,9.8 16.4,6.6"></polyline>
        <path d="M18.2 2.6 L21.4 5.8 L18.2 9 L15 5.8 Z" style={{"fill":"currentColor","stroke":"currentColor"}}></path>
        <line x1="6" y1="22.2" x2="6" y2="19.4"></line>
        <line x1="9.2" y1="22.2" x2="9.2" y2="19.4"></line>
        <line x1="12.4" y1="22.2" x2="12.4" y2="19.4"></line>
      </g></>),
  "m-repeat": (<><g style={{"fill":"none","stroke":"currentColor","strokeWidth":"1.7","strokeLinecap":"round","strokeLinejoin":"round"}}>
        <line x1="3" y1="3" x2="3" y2="17"></line>
        <line x1="6.4" y1="16.6" x2="15.4" y2="7"></line>
        <path d="M18 2.8 L21 5.8 L18 8.8 L15 5.8 Z"></path>
        <path d="M20.4 8.6 C22.4 13.4 18.6 17.4 14.4 17.8"></path>
        <line x1="6" y1="22.2" x2="6" y2="19.4"></line>
        <line x1="9.2" y1="22.2" x2="9.2" y2="19.4"></line>
      </g></>),
  "m-attempt": (<><g style={{"fill":"none","stroke":"currentColor","strokeWidth":"1.7","strokeLinecap":"round","strokeLinejoin":"round"}}>
        <line x1="3" y1="3" x2="3" y2="17"></line>
        <line x1="6.4" y1="16.6" x2="12.6" y2="10"></line>
        <polyline points="21,8.6 21,4 16.4,4"></polyline>
        <line x1="6" y1="22.2" x2="6" y2="19.4"></line>
        <line x1="9.2" y1="22.2" x2="9.2" y2="19.4"></line>
        <line x1="12.4" y1="22.2" x2="12.4" y2="19.4"></line>
        <line x1="15.6" y1="22.2" x2="15.6" y2="19.4"></line>
      </g></>),
  "d-boulder": (<><g style={{"fill":"none","stroke":"currentColor","strokeWidth":"1.7","strokeLinecap":"round","strokeLinejoin":"round"}}>
        <path d="M3.5 17.5 L6.5 7 L13 3.6 L19 8.4 L20.5 17.5 Z"></path>
        <path d="M6.5 7 L12.4 9.6 L19 8.4"></path>
        <line x1="12.4" y1="9.6" x2="12.4" y2="17.5"></line>
        <line x1="2" y1="20.6" x2="22" y2="20.6" style={{"strokeDasharray":"3 2.4"}}></line>
      </g></>),
  "d-sport": (<><g style={{"fill":"none","stroke":"currentColor","strokeWidth":"1.7","strokeLinecap":"round","strokeLinejoin":"round"}}>
        <line x1="4" y1="2.6" x2="4" y2="21.4"></line>
        <circle cx="8.4" cy="5.6" r="1.5"></circle>
        <circle cx="8.4" cy="12" r="1.5"></circle>
        <circle cx="8.4" cy="18.4" r="1.5"></circle>
        <line x1="4" y1="5.6" x2="6.9" y2="5.6"></line>
        <line x1="4" y1="12" x2="6.9" y2="12"></line>
        <line x1="4" y1="18.4" x2="6.9" y2="18.4"></line>
        <path d="M9.9 5.6 C14 6.6 14 11 9.9 12 C14 13 14 17.4 9.9 18.4 C13 19.4 15.6 20.6 16.6 22"></path>
      </g></>),
  "d-trad": (<><g style={{"fill":"none","stroke":"currentColor","strokeWidth":"1.7","strokeLinecap":"round","strokeLinejoin":"round"}}>
        <path d="M8 2 L10.4 7.4 L8.6 12.4 L11 17 L9.4 22"></path>
        <path d="M13 6.4 C16 5 18.6 6.6 18.4 8.6"></path>
        <path d="M13.4 10.6 C16.6 10.4 18.8 8.8 18.4 6.6"></path>
        <line x1="14.4" y1="8.6" x2="18.6" y2="14.4"></line>
        <path d="M14.6 16.6 L18.6 14.4 L20 18.6 L16 20.4 Z"></path>
      </g></>),
  "d-toprope": (<><g style={{"fill":"none","stroke":"currentColor","strokeWidth":"1.7","strokeLinecap":"round","strokeLinejoin":"round"}}>
        <line x1="3" y1="3.4" x2="21" y2="3.4" style={{"strokeWidth":"2.4"}}></line>
        <rect x="10" y="5" width="4" height="5.4" rx="2"></rect>
        <path d="M10.4 10.4 C8 14 8.6 18.6 10.4 22"></path>
        <path d="M13.6 10.4 C17 13.4 16 18.6 13.2 22"></path>
        <line x1="6" y1="3.4" x2="6" y2="6"></line>
        <line x1="18" y1="3.4" x2="18" y2="6"></line>
      </g></>),
  "d-other": (<><g style={{"fill":"none","stroke":"currentColor","strokeWidth":"1.7","strokeLinecap":"round","strokeLinejoin":"round"}}>
        <polyline points="7,3 4,3 4,21 7,21"></polyline>
        <polyline points="17,3 20,3 20,21 17,21"></polyline>
        <line x1="9" y1="7.4" x2="15" y2="7.4"></line>
        <line x1="9" y1="12" x2="13" y2="12"></line>
        <line x1="9" y1="16.6" x2="15" y2="16.6"></line>
      </g></>),
  "e-indoor": (<><g style={{"fill":"none","stroke":"currentColor","strokeWidth":"1.7","strokeLinecap":"round","strokeLinejoin":"round"}}>
        <rect x="3" y="3" width="18" height="18" rx="1"></rect>
        <line x1="3" y1="12" x2="21" y2="12"></line>
        <line x1="12" y1="3" x2="12" y2="21"></line>
        <path d="M6 9.4 L8.4 5.6 L9.8 9.4 Z" style={{"fill":"currentColor"}}></path>
        <circle cx="16" cy="7.4" r="1.4" style={{"fill":"currentColor","stroke":"none"}}></circle>
        <circle cx="7.6" cy="16.6" r="1.4" style={{"fill":"currentColor","stroke":"none"}}></circle>
        <circle cx="16.6" cy="16.4" r="1.4" style={{"fill":"currentColor","stroke":"none"}}></circle>
      </g></>),
  "e-outdoor": (<><g style={{"fill":"none","stroke":"currentColor","strokeWidth":"1.7","strokeLinecap":"round","strokeLinejoin":"round"}}>
        <polyline points="2,18.4 6.4,10.6 9.4,14 14,4.6 18.4,12.4 22,18.4"></polyline>
        <path d="M14 4.6 L13 10 L15.4 13.4 L14.4 18.4"></path>
        <line x1="2" y1="21.4" x2="22" y2="21.4"></line>
        <circle cx="6" cy="5" r="2.2"></circle>
      </g></>),
  "i-cloud": (<><g style={{"fill":"none","stroke":"currentColor","strokeWidth":"1.7","strokeLinecap":"round","strokeLinejoin":"round"}}>
        <path d="M7 18h10a4 4 0 0 0 .6-7.96A6 6 0 0 0 6.2 9.2 4.4 4.4 0 0 0 7 18Z"></path>
        <line x1="12" y1="14.6" x2="12" y2="9.4"></line>
        <polyline points="9.6,11.6 12,9.2 14.4,11.6"></polyline>
      </g></>),
};

function Glyph({ id, className, title }: { id: string; className?: string; title?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} role={title ? "img" : "presentation"}
         aria-label={title} aria-hidden={title ? undefined : true} focusable="false">
      {title ? <title>{title}</title> : null}
      {P[id]}
    </svg>
  );
}

/** Ascent style — the 5 that carry meaning. 8a.nu scores these; we draw them. */
export function AscentMark({ ascent, sent, className = "mark", title }:
  { ascent: MarkId; sent?: boolean; className?: string; title?: string }) {
  const tone = ascent === "attempt" || sent === false ? "mark--attempt" : "mark--send";
  return <Glyph id={"m-" + ascent} className={className + " " + tone} title={title ?? ascent} />;
}

export function DisciplineMark({ discipline, className = "mark", title }:
  { discipline: DisciplineId; className?: string; title?: string }) {
  return <Glyph id={"d-" + discipline} className={className} title={title} />;
}

export function EnvMark({ env, className = "mark", title }:
  { env: EnvId; className?: string; title?: string }) {
  return <Glyph id={"e-" + env} className={className} title={title} />;
}

export function CloudMark({ className = "mark" }: { className?: string }) {
  return <Glyph id="i-cloud" className={className} />;
}

/** Every key the code can request — the P7 art-coverage assertion checks against these. */
export const MARK_KEYS = Object.keys(P);
export const ASCENTS: MarkId[] = ["onsight", "flash", "redpoint", "repeat", "attempt"];
export const DISCIPLINES: DisciplineId[] = ["boulder", "sport", "trad", "toprope", "other"];
export const ENVS: EnvId[] = ["indoor", "outdoor"];
